import { useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { Issue, IssueLevel } from "generated-graphql/graphql";
import { useState } from "react";
import { hasCriticalIssues } from "util/forms";

import {
  ATTACH_SHIPMENT_BILL_OF_LADING_MUTATION,
  BILL_OF_LADING_LINK_REFETCH_QUERIES,
  VALIDATE_ATTACH_SHIPMENT_BILL_OF_LADING_INPUT,
} from "./api";
import { CandidateShippingDocument, ShippingDocumentPicker } from "./ShippingDocumentPicker";
import { UploadFallback } from "./UploadFallback";

/**
 * Pick the bill of lading that came back for this shipment.
 *
 * No signed-copy flow here, unlike `AttachManifestDialog`: a bill of lading
 * has no unsigned-draft-then-signed-copy distinction, so picking one and
 * attaching it is all this dialog does. A document that is not on file yet is
 * handed off to the upload flow through `onUploadInstead`, the same way the
 * manifest picker does it.
 */
export function AttachBillOfLadingDialog({
  shipmentId,
  facilityId,
  onClose,
  onUploadInstead,
  replacingDocumentNumber = null,
}: {
  shipmentId: string;
  facilityId: string;
  onClose: () => void;
  /**
   * Hands off to the upload flow when the bill of lading is not on file yet.
   * Omitted when there is nowhere to hand off to, which is what keeps the
   * offer from appearing on a surface that cannot honour it.
   */
  onUploadInstead?: () => void;
  /**
   * The document currently linked, when this dialog was opened to replace it.
   * Linking is an upsert, so the swap is silent unless it is spelled out.
   */
  replacingDocumentNumber?: string | null;
}) {
  const alerts = useAlerts();
  // Held as the option itself, not as an id looked back up in a fetched page.
  // The picker owns its own paging, so a remembered id could point at a
  // document no longer in the options it currently holds.
  const [selected, setSelected] = useState<CandidateShippingDocument | null>(null);

  const { data: validation, loading: validating } = useQuery(
    VALIDATE_ATTACH_SHIPMENT_BILL_OF_LADING_INPUT,
    {
      variables: {
        input: { shipmentId, shippingDocumentId: selected?.id ?? "" },
      },
      skip: !selected,
      fetchPolicy: "network-only",
    }
  );
  const issues = selected ? (validation?.attachShipmentBillOfLadingInputValidator ?? []) : [];
  const blocked = hasCriticalIssues(issues);

  const [attach, { loading: attaching }] = useMutation(ATTACH_SHIPMENT_BILL_OF_LADING_MUTATION, {
    refetchQueries: BILL_OF_LADING_LINK_REFETCH_QUERIES,
  });

  const handleAttach = async () => {
    if (!selected || blocked) {
      return;
    }
    try {
      await attach({
        variables: { input: { shipmentId, shippingDocumentId: selected.id } },
      });
      alerts.success("Bill of lading attached.");
      onClose();
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to attach the bill of lading.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={replacingDocumentNumber ? "Replace this bill of lading" : "Attach a bill of lading"}
      primaryActionButtonProps={{
        saveText: replacingDocumentNumber ? "Replace document" : "Attach document",
        loading: attaching,
        disabled: !selected || validating || blocked,
        onClick: handleAttach,
      }}
    >
      <Stack spacing={2}>
        {replacingDocumentNumber && (
          <Alert severity="warning">
            This shipment is already linked to {replacingDocumentNumber}. Attaching another document
            releases that one.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          Shipping documents on file for this facility.
        </Typography>
        <ShippingDocumentPicker facilityId={facilityId} value={selected} onChange={setSelected} />

        {onUploadInstead && (
          <UploadFallback
            onUpload={onUploadInstead}
            // Leads with the step the click actually takes, because the click
            // swaps this dialog for the upload one rather than opening a file
            // browser, and nothing on screen said which. The facility is part of
            // that step: a bill of lading carries no EPA ID to infer one from.
            //
            // Unlike a manifest, this one does not come back to the list to be
            // linked later: the reviewer confirms what was read and it attaches
            // to this shipment as part of the same pass.
            outcome="You'll choose a facility and a file, then confirm what Encamp reads off it before it attaches here."
          />
        )}

        <AttachIssueList issues={issues} />
      </Stack>
    </Dialog>
  );
}

/**
 * Why the chosen document cannot be attached, if it cannot. Same pattern as
 * AttachManifestDialog's AttachIssueList.
 */
function AttachIssueList({ issues }: { issues: readonly Issue[] }) {
  if (issues.length === 0) {
    return null;
  }
  return (
    <Box>
      {issues.map((issue, index) => (
        <Alert
          key={`${issue.message}-${index}`}
          severity={issue.level === IssueLevel.Critical ? "error" : "warning"}
          sx={{ mt: 1 }}
        >
          {issue.message}
        </Alert>
      ))}
    </Box>
  );
}
